import React, { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { t } from 'i18next';
import moment from 'moment';
import { FiChevronDown, FiClock, FiUsers } from 'react-icons/fi';
import { useAuditorium } from '@/hooks/useAuditorium';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import { useSession } from '@/hooks/useSession';
import usePermission from '@/hooks/usePermission';
import { confirm } from '@/lib/confirm';
import { PlayfulCard, PlayfulCardButton } from '@/components/custom/playful-card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import monitoriaIcon from '@/assets/images/icons/monitoria.png';

export function AuditoriumPlayfulCard({ auditorium, setActiveAuditorium }) {
  const navigate = useNavigate();
  const { deleteAuditorium, updateAuditorium } = useAuditorium();
  const isAdmin = !useCheckIncludeProfiles({ only: ['student'] });
  const currentUser = useSession().session.user;
  const isOnline = auditorium.status === 'Online';

  const auditoriumForm = useFormik({
    initialValues: {
      published: auditorium?.published
    }
  });

  const handleChangePublishedAuditorium = async () => {
    auditoriumForm.setFieldValue('published', !auditoriumForm.values.published);
    await updateAuditorium({ published: !auditoriumForm.values.published }, auditorium.id);
  };

  const handleDeleteAuditorium = async () => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteAuditorium'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });

    if (confirmed) {
      deleteAuditorium(auditorium.id);
    }
  };

  const shouldRenderOptions = useMemo(() => {
    if (auditorium?.['authored-by-user'] === true) {
      if (auditorium?.creator?.id !== currentUser.id) {
        return false;
      } else return true;
    }

    if (auditorium?.['authored-by-user'] === false) {
      if (usePermission('Editar monitorias', 'Monitorias') || usePermission('Excluir monitorias', 'Monitorias')) {
        return true;
      } else return false;
    }
  }, [auditorium]);

  const isPublished = auditoriumForm.values.published;
  const isScheduledForFuture = auditorium['published-at'] && moment(auditorium['published-at']).isAfter(moment());

  const badges = isAdmin
    ? [
        { text: isPublished ? t('textsCommon.published') : t('textsCommon.unpublished'), variant: isPublished ? 'success-soft' : 'muted' },
        { text: auditorium.status, variant: isOnline ? 'success-soft' : 'muted' }
      ]
    : { text: auditorium.status, variant: isOnline ? 'success-soft' : 'muted' };

  return (
    <PlayfulCard
      className="p-6"
      badge={badges}
    >
      <img
        src={monitoriaIcon}
        alt=""
        aria-hidden="true"
        className="h-20 w-20 object-contain"
      />

      <h3
        className="mb-0 mt-3 line-clamp-2 text-base font-extrabold text-neutral-900"
        title={auditorium.name}
      >
        {auditorium.name}
      </h3>

      {auditorium?.['auditorium-lesson']?.name && (
        <p
          className="mb-0 mt-0.5 truncate text-sm text-neutral-dark"
          title={auditorium['auditorium-lesson'].name}
        >
          {auditorium['auditorium-lesson'].name}
        </p>
      )}

      <div className="mb-6 mt-4 flex flex-1 flex-wrap items-center gap-4 text-sm text-neutral-500">
        <button
          type="button"
          onClick={() => setActiveAuditorium(auditorium)}
          className="flex items-center gap-1.5 font-semibold transition-opacity hover:opacity-70"
        >
          <FiUsers className="h-4 w-4 text-[var(--color-mission-medium)]" />
          {auditorium?.['student-count'] ?? 0} participantes
        </button>

        {isAdmin && isScheduledForFuture && (
          <span className="flex items-center gap-1.5 font-semibold">
            <FiClock className="h-4 w-4 text-[var(--color-mission-medium)]" />
            Publicação agendada para {moment(auditorium['published-at']).format('DD/MM/YYYY')}
          </span>
        )}
      </div>

      <div className="flex w-full items-center gap-2">
        <PlayfulCardButton
          className="flex-1"
          onClick={() => navigate(`/auditorio/${auditorium.id}`)}
        >
          Acessar
        </PlayfulCardButton>

        {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <PlayfulCardButton
                variant="outline"
                aria-label={t('button.options')}
                className="h-9 w-9 flex-none gap-0 px-0"
              >
                <FiChevronDown size={20} />
              </PlayfulCardButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleChangePublishedAuditorium}>{auditoriumForm.values.published ? t('lms.unpublish') : t('lms.publish')}</DropdownMenuItem>
              {shouldRenderOptions && usePermission('Editar monitorias', 'Monitorias') && (
                <DropdownMenuItem asChild>
                  <Link to={`/auditorio/editar/nome/${auditorium.id}`}>{t('button.edit')}</Link>
                </DropdownMenuItem>
              )}
              {shouldRenderOptions && usePermission('Excluir monitorias', 'Monitorias') && <DropdownMenuItem onClick={handleDeleteAuditorium}>{t('button.delete')}</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </PlayfulCard>
  );
}
